/**
 * UC-002 Budget admin schemas (Phase 16).
 *
 * Type contracts pro `/api/admin/budgets` REST endpoints — list, detail,
 * unassigned-spz, approve, return-to-draft, resolve-unassigned-spz.
 *
 * Per DEC-011 — entity names PascalCase, DB tabulky snake_case; JSON payloady
 * v snake_case (REQ-API-012). Princip #2 — backend nesmí FX přepočet, tedy
 * EUR + CZK paralelně + `eur_advisory_flag` boolean.
 */

export const BUDGET_STATUS = ["draft", "approved", "returned"] as const;
export type BudgetStatus = (typeof BUDGET_STATUS)[number];

export interface BudgetListRow {
  budget_id: string;
  billing_group_id: string;
  billing_group_code: string;
  zone_code: string;
  month: string; // ISO "YYYY-MM"
  status: BudgetStatus;
  approved_at: string | null;
  approved_by: string | null;
  total_amount_kc: string;
  total_amount_eur: string;
  eur_advisory_flag: boolean;
  sync_complete: boolean;
  unassigned_count: number;
  item_count: number;
}

export interface BudgetListResponse {
  rows: BudgetListRow[];
}

export interface BudgetItemTransaction {
  fuel_transaction_id: string;
  occurred_at: string;
  fuel_card_id: string | null;
  /** SPZ ("Název karty") — jen popisek řádku, ne klíč párování. */
  spz: string | null;
  amount_kc: string;
  amount_eur: string | null;
  liters: string | null;
  location: string | null;
  note: string | null;
}

/** Položka rozpočtu = tankovací karta (jednotka atribuce). */
export interface BudgetCardItem {
  fuel_card_id: string;
  /** SPZ z "Název karty" — jen výpis. */
  card_label: string | null;
  card_number_display: string | null;
  /** Atribuce karty = holder (PO/FO). */
  holder_id: string | null;
  /** holder.display_name — pokryje PO i FO. */
  holder_label: string | null;
  amount_kc: string;
  amount_eur: string;
  transaction_count: number;
  transactions: BudgetItemTransaction[];
}

export interface BudgetDetailResponse {
  budget_id: string;
  billing_group_id: string;
  billing_group_code: string;
  zone_code: string;
  month: string;
  status: BudgetStatus;
  approved_at: string | null;
  approved_by: string | null;
  total_amount_kc: string;
  total_amount_eur: string;
  eur_advisory_flag: boolean;
  sync_complete: boolean;
  items: BudgetCardItem[];
}

export interface UnassignedCardTransaction {
  fuel_transaction_id: string;
  occurred_at: string;
  card_number_display: string | null;
  spz: string | null;
  amount_kc: string;
  amount_eur: string | null;
}

export interface UnassignedCardGroup {
  card_number_display: string | null;
  transaction_count: number;
  total_amount_kc: string;
  transactions: UnassignedCardTransaction[];
}

export interface UnassignedCardsResponse {
  budget_id: string;
  groups: UnassignedCardGroup[];
}

export interface CardCandidate {
  fuel_card_id: string;
  card_label: string | null;
  card_number_display: string | null;
  /** Aktuální držitel karty (holder_id) — null = bez držitele. */
  holder_id: string | null;
  holder_label: string | null;
}

export interface CardCandidatesResponse {
  candidates: CardCandidate[];
}

export interface ApproveBudgetResponse {
  budget_id: string;
  status: "approved";
  approved_at: string;
  approved_by: string;
  total_amount_kc: string;
  total_amount_eur: string;
  eur_advisory_flag: boolean;
  item_count: number;
}

/** Mapování fuel_card_id → holder_id. */
export interface AssignCardHolderPayload {
  holder_assignments: Record<string, string>;
}

export interface AssignCardHolderResponse {
  budget_id: string;
  cards_updated: number;
}

export interface ReturnBudgetPayload {
  reason: string;
}

export interface ReturnBudgetResponse {
  budget_id: string;
  status: "returned";
  returned_at: string;
}

/**
 * UC-002 Krok 1 — eligible (měsíc, Středisko) pro "Nový rozpočet" dialog.
 * Backend filtruje ne-eligible kombinace (sync != KOMPLETNI, měsíc neuzavřen,
 * budget už existuje); FE jen render dropdown.
 */
export interface AvailableBudgetTarget {
  billing_group_id: string;
  billing_group_code: string;
  zone_code: string;
  month: string; // ISO "YYYY-MM"
}

export interface AvailableBudgetTargetsResponse {
  targets: AvailableBudgetTarget[];
}

export interface CreateBudgetRequest {
  billing_group_id: string;
  month: string; // ISO "YYYY-MM"
}

export interface CreateBudgetResponse {
  budget_id: string;
  billing_group_id: string;
  billing_group_code: string;
  zone_code: string;
  month: string;
  status: "draft";
  total_amount_kc: string;
  total_amount_eur: string;
  eur_advisory_flag: boolean;
  item_count: number;
  unassigned_count: number;
}

/** Composite key pro <option value> v Nový rozpočet dropdown. */
export function encodeBudgetTargetKey(t: AvailableBudgetTarget): string {
  return `${t.month}|${t.billing_group_id}`;
}

export function decodeBudgetTargetKey(
  key: string,
): { month: string; billing_group_id: string } | null {
  const [month, billing_group_id] = key.split("|");
  if (!month || !billing_group_id) return null;
  return { month, billing_group_id };
}

/**
 * Konstanta pro 3-stupňový Confirm Dialog (REQ-BIZ-009 + princip #3).
 *
 * Admin musí napsat exact-match české "potvrzuji nevratně" pro povolení
 * approve mutation. Diakritika preservovaná; case-insensitive porovnání,
 * trim whitespace.
 */
export const BUDGET_APPROVE_PHRASE = "potvrzuji nevratně";

export function isApprovePhraseValid(input: string): boolean {
  return input.trim().toLocaleLowerCase("cs-CZ") === BUDGET_APPROVE_PHRASE;
}

/**
 * Formátovač měsíce v cs-CZ (např. "2026-05" → "Květen 2026").
 */
export function formatBudgetMonth(month: string): string {
  const match = /^(\d{4})-(\d{2})$/.exec(month);
  if (!match) return month;
  const year = Number(match[1]);
  const monthIdx = Number(match[2]) - 1;
  if (Number.isNaN(year) || monthIdx < 0 || monthIdx > 11) return month;
  const date = new Date(Date.UTC(year, monthIdx, 1));
  try {
    const label = date.toLocaleString("cs-CZ", {
      year: "numeric",
      month: "long",
      timeZone: "UTC",
    });
    return label.charAt(0).toLocaleUpperCase("cs-CZ") + label.slice(1);
  } catch {
    return month;
  }
}

/**
 * Formátování CZK / EUR částek z plain-decimal stringů backendu.
 * Žádný FX přepočet (princip #2).
 */
export function formatKc(raw: string | null | undefined): string {
  if (raw == null || raw === "") return "—";
  const n = Number(raw);
  if (!Number.isFinite(n)) return raw;
  return n.toLocaleString("cs-CZ", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }) + " Kč";
}

export function formatEur(raw: string | null | undefined): string {
  if (raw == null || raw === "") return "—";
  const n = Number(raw);
  if (!Number.isFinite(n)) return raw;
  return n.toLocaleString("cs-CZ", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }) + " €";
}
